/**
 * Content Schema 内容处理工具函数
 * ONLY supports Content Schema format
 */

import { CONTENT_TYPE_CONTENT_SCHEMA, CONTENT_TYPE_MARKDOWN } from "@/constants/content-schema";
import { NODE_TYPE_ENTITY } from "@/constants/lexical";

interface ContentSchemaNodeLike {
  type?: string;
  text?: string;
  altText?: string;
  symbol?: string;
  name?: string;
  children?: unknown[];
}

function asContentNode(value: unknown): ContentSchemaNodeLike | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as ContentSchemaNodeLike;
}

/**
 * 检测内容类型是 Content Schema 还是 Markdown
 * @param content - Memo 内容字符串
 * @returns ContentType 常量
 *
 * Note: 后端会根据此字段判断是否需要提取实体。
 * Content Schema 格式: {"type":"root","version":1,...}
 */
export function detectContentType(content: string): typeof CONTENT_TYPE_CONTENT_SCHEMA | typeof CONTENT_TYPE_MARKDOWN {
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === "object" && parsed.type === "root") {
      return CONTENT_TYPE_CONTENT_SCHEMA;
    }
  } catch {
    // noop
  }

  // 否则默认为 Markdown
  return CONTENT_TYPE_MARKDOWN;
}

/**
 * 解析 Content Schema
 * @param content - Memo 内容字符串
 * @returns 解析后的根节点或 null
 */
export function parseLexicalContent(content: string) {
  try {
    const contentSchema = JSON.parse(content);

    // Content Schema format: { type: "root", version: 1, children: [...] }
    if (contentSchema.type === "root" && typeof contentSchema === "object") {
      return contentSchema;
    }
    return null;
  } catch (error) {
    console.error("Failed to parse Content Schema:", error);
    return null;
  }
}

/**
 * 从 Content Schema 中提取纯文本（用于搜索、snippet 等）
 * @param contentSchemaNode - Content Schema 节点
 * @returns 纯文本字符串
 */
export function extractTextFromLexical(contentSchemaNode: unknown): string {
  if (!contentSchemaNode) return "";

  function extractFromNode(node: unknown): string {
    const nodeData = asContentNode(node);
    if (!nodeData) {
      return "";
    }

    let text = "";

    // 图片节点
    if (nodeData.type === NODE_TYPE_ENTITY.IMAGE) {
      return nodeData.altText ? `[图片: ${nodeData.altText}]` : "[图片]";
    }

    // Stock 节点
    if (nodeData.type === NODE_TYPE_ENTITY.STOCK) {
      return `$${nodeData.symbol || ""}`;
    }

    // Tag 节点
    if (nodeData.type === NODE_TYPE_ENTITY.TAG) {
      return `#${nodeData.name || ""}`;
    }

    // 如果节点有 text 属性（文本节点）
    if (nodeData.text) {
      text += nodeData.text;
    }

    // 递归处理子节点
    // Content Schema: children are in node.children
    const children = nodeData.children;
    if (children && Array.isArray(children)) {
      for (const child of children) {
        text += extractFromNode(child);
      }
    }

    return text;
  }

  return extractFromNode(contentSchemaNode);
}
